"use client";

import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

/**
 * "On this page" for a post: the headings as a vertical rail of nodes joined
 * by a line, pinned to the right of the article. As the reader scrolls, the
 * line fills from the top to where they are, nodes they have passed turn
 * solid, and the current heading is set in full ink. Each entry is an anchor
 * to its heading, so the rail is navigation as well as a progress mark.
 *
 * Position is measured, not observed: a scroll listener (throttled to one
 * frame) compares each heading's document offset with a reading line a third
 * of the way down the viewport, and the fill runs to the fraction of the way
 * between the current heading and the next (or the end of the article after
 * the last). An IntersectionObserver would only give discrete steps.
 *
 * Desktop only (`lg` and up). On narrower screens the column is not there and
 * the headings are a scroll away; a collapsed version added nothing.
 */
export type TocItem = { id: string; text: string };

/** The reading line: this far down the viewport counts as "here". */
const LINE = 0.32;

export function BlogToc({ items, endId }: { items: TocItem[]; endId: string }) {
  const listRef = useRef<HTMLOListElement>(null);
  const [state, setState] = useState<{ active: number; fill: number }>({ active: -1, fill: 0 });

  useEffect(() => {
    let raf = 0;
    const update = () => {
      raf = 0;
      const list = listRef.current;
      if (!list) return;
      const heads = items.map((it) => document.getElementById(it.id));
      const end = document.getElementById(endId);
      const nodes = Array.from(list.querySelectorAll<HTMLElement>("[data-toc-node]"));
      if (heads.some((h) => !h) || nodes.length !== items.length) return;

      const ref = window.scrollY + window.innerHeight * LINE;
      const tops = heads.map((h) => h!.getBoundingClientRect().top + window.scrollY);
      const endTop = end ? end.getBoundingClientRect().top + window.scrollY : tops[tops.length - 1] + 1;

      let active = -1;
      for (let i = 0; i < tops.length; i++) if (tops[i] <= ref) active = i;

      // the fill, in px down the list: to the active node, then part-way to the next
      const listTop = list.getBoundingClientRect().top;
      const dotY = nodes.map((n) => n.getBoundingClientRect().top - listTop + n.offsetHeight / 2);
      let fill = 0;
      if (active >= 0) {
        const next = active + 1 < tops.length ? tops[active + 1] : endTop;
        const span = Math.max(1, next - tops[active]);
        const frac = Math.min(1, Math.max(0, (ref - tops[active]) / span));
        const to = active + 1 < dotY.length ? dotY[active + 1] : dotY[active];
        fill = dotY[active] + (to - dotY[active]) * frac;
      }
      setState((s) => (s.active === active && Math.abs(s.fill - fill) < 0.5 ? s : { active, fill }));
    };
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [items, endId]);

  if (items.length < 2) return null;

  return (
    <nav aria-label="On this page" className="font-sans">
      <p className="eyebrow mb-5">On this page</p>
      <ol ref={listRef} className="relative">
        {/* the track, and the part of it the reader has covered */}
        <span aria-hidden="true" className="absolute bottom-3 left-[5px] top-3 w-px bg-border" />
        <span
          aria-hidden="true"
          className="absolute left-[5px] top-3 w-px bg-foreground transition-[height] duration-150 ease-out motion-reduce:transition-none"
          style={{ height: `${Math.max(0, state.fill - 12)}px` }}
        />
        {items.map((it, i) => {
          const passed = i < state.active;
          const active = i === state.active;
          return (
            <li key={it.id} className="relative">
              <a
                href={`#${it.id}`}
                aria-current={active ? "location" : undefined}
                className={cn(
                  "group flex items-start gap-4 py-2 text-[0.8rem] leading-snug transition-colors duration-200",
                  active ? "text-foreground" : passed ? "text-foreground/70 hover:text-foreground" : "text-muted hover:text-foreground",
                )}
              >
                <span
                  data-toc-node
                  aria-hidden="true"
                  className={cn(
                    "relative mt-[0.28rem] h-[11px] w-[11px] shrink-0 rounded-full border transition-colors duration-200",
                    active || passed ? "border-foreground bg-foreground" : "border-border-strong bg-background group-hover:border-foreground",
                  )}
                >
                  {active && (
                    <span className="absolute inset-[-4px] rounded-full border border-foreground/30" />
                  )}
                </span>
                <span>{it.text}</span>
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
